"use client"

import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Box,
  Button,
  Checkbox,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  TextFieldProps,
} from "@mui/material"
import {
  InitIframeTransactionData,
  InitIframeTransactionRequest,
  postWebTransactionsInitIframe,
} from "api"
import { useLocale, useTranslations } from "next-intl"
import { LegacyRef, useId, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { CheckboxIcon, CheckedCheckboxIcon, NewText, SuccessFailState, XIcon } from "ui"
import { sharedColors as colors } from "ui/theme/colors"
import { submitClick, submitError } from "utils"
import { generateRentAppartmentPaymentSchema } from "utils/Form/FormSchema"
import * as yup from "yup"
import { ctaMessage } from "../../../Contact/ContactForm"
import CustomHelperText from "../../../Contact/CustomHelperText"
import { ProjectsMessages } from "../../Projects"
import PaymentIFrameRenderer from "./PaymentIFrameRenderer"

// eslint-disable-next-line prefer-destructuring
const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}
export type FormData = {
  price: number | undefined
  currency: string | undefined
  area: number | undefined
  floor: string | undefined
  rooms: string | undefined
  assetName?: string
  apartmentNumber?: string | number
  buildingName?: string
}

export type PaymentFormProps = {
  formSubjects: Record<string, string>
  onClose: () => void
  messages: ProjectsMessages
  formData: FormData
  bmbyId?: number
  title?: string
  advancedPaymentTitle?: string
  type?: string
  paymentForm?: boolean
}

const StyledCheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }
  span div {
    background-color: ${({ theme }) => theme.colors.blue4rentMarketing} !important;
  }
`

const CheckBoxesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`

const FormContainer = styled.div`
  --text-black: #011727;
  min-width: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) => props.theme.breakpoints.down("md")} {
    min-width: 0;
    width: 100%;
    height: 100%;
  }
  &.newStyle {
    height: 100%;
    position: relative;
    flex-direction: column;
    min-width: unset;
    overflow: hidden;
  }
`
const StyledForm = styled.form`
  ${(props) => props.theme.breakpoints.down("md")} {
    height: 100%;
  }
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  border: 2px solid ${(props) => props.theme.colors.propBlue};
  background: white;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 660px;
  }
  .MuiFormHelperText-root {
    bottom: -25px;
    line-height: 1;
  }
  &.newStyle {
    border-radius: 0;
    border: none;
    height: 100%;
    overflow: auto;
    flex: 1;
    width: 100%;
    .MuiFilledInput-root.MuiInputBase-multiline {
      padding-top: 8px !important;
    }
    .MuiInputBase-input:not(.MuiInputBase-inputMultiline) {
      padding-top: 8px !important;
    }
    .MuiInputBase-inputMultiline {
    }
  }
`

const StyledInsideForm = styled.div`
  padding: 24px;
  /* max-height: 500px; */
  overflow-y: scroll;

  ${(props) => props.theme.breakpoints.up("md")} {
    max-height: none;
    overflow-y: auto;
    max-height: 600px;
  }

  &.newStyle {
    flex: 1;
    max-height: unset;
  }
`
const StyledHeadingH2 = styled.h2`
  font-size: 32px;
  font-weight: 400;
  line-height: 150%;
  margin: 0;
  display: flex;
  width: max-content;
  flex-direction: column;
  width: 1px;
  flex: 1;
  max-width: max-content;
  &::after {
    content: "";
    background-image: url("/icons/heading-underline-blue-mobile.svg");
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    display: flex;
    width: 100%;
    height: 4px;
    position: relative;
    top: -4px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px; /* 90px */
    letter-spacing: 0.6px;
    &::after {
      background-image: url("/icons/heading-underline-blue-desktop.svg");
    }
  }
`

const StyledHeader = styled.div`
  height: 84px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => props.theme.colors.propBlue};
  border-radius: 10px 10px 0 0;
  display: flex;
  padding: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 24px;
  }
  &.newStyle {
    height: unset;
    border-radius: 0;
    background-color: white;
    color: var(--text-black);
    align-items: flex-start;
    z-index: 1;
    position: relative;
    flex-shrink: 0;
    width: 100%;
  }
`

const BottomContainer = styled.div<{ withFormData?: boolean }>`
  border-top: 1px solid black;
  padding: 24px;
  display: flex;
  gap: 16px;
  ${(props) => props.theme.breakpoints.down("sm")} {
    .grecaptcha-badge {
      height: 45px !important;
    }
  }

  .grecaptcha-badge {
    visibility: hidden;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: stretch;
    gap: 24px;
    margin-bottom: 0px;
  }

  &.newStyle {
    border-top: none;
    background-color: ${({ theme }) => theme.colors.darkBlue4rentMarketing};
    color: ${({ withFormData }) => (withFormData ? "white" : "")};
    ${(props) => props.theme.breakpoints.up("md")} {
      /* padding-inline-start: 90px; */
      background-color: ${({ withFormData, theme }) =>
        withFormData ? theme.colors.darkBlue4rentMarketing : "transparent"};
    }
  }
`
const TopDetailsDiv = styled.div`
  display: flex;
  text-align: center;
  flex-direction: column;
  height: 61px;

  ${(props) => props.theme.breakpoints.down("sm")} {
    height: 55px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: center;
    width: max-content;
    flex-direction: row;
    height: unset;
  }
`

const DetailNumbers = styled.div`
  display: flex;
  gap: 8px;
`
const BottomDetailsDiv = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: end;
  }

  .grecaptcha-badge {
    ${(props) => props.theme.breakpoints.down("md")} {
      bottom: -11px !important;
    }
  }
`
const PriceDiv = styled.div`
  white-space: nowrap;
`
const StyledTitle = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: boolean }>`
  padding: 1px 10px 0px 0;
  ${(props) =>
    !props.status
      ? `  rect {
    stroke: red;
  }`
      : ""}
`

const StyledButtonNew = styled(Button)`
  border-radius: 8px;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0px;
  padding: 12px 5px;
  justify-content: center;
  min-width: 200px;
`

const PriceStrong = styled.strong`
  font-size: 40px;
  line-height: 150%;
  ${(props) => props.theme.breakpoints.down("md")} {
    font-size: 30px;
    line-height: 45px;
    font-weight: 400;
  }
`

const BlueStyledButton = styled(StyledButtonNew)<{ fixedWidth?: boolean }>`
  --theme-color: ${(props) => props.theme.colors.blue4rentMarketing};
  color: white;
  background-color: var(--theme-color);
  border-color: var(--theme-color);
  flex: ${({ fixedWidth }) => (fixedWidth ? "unset" : "1")};
  &[disabled] {
    color: white;
    background-color: ${(props) => props.theme.colors.gray4rentMarketing};
  }

  align-self: flex-end;
  min-width: 169px;
  ${(props) => props.theme.breakpoints.down("sm")} {
    min-width: 94px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    width: unset;
    align-self: unset;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.blue4rentMarketingHover};
  }
`

const SmallDivider = styled.div<{ mobileHide?: boolean }>`
  height: 14px;
  display: ${(props) => (props.mobileHide ? "none" : "block")};
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 24px;
    display: block;
  }
  width: 1px;
  background-color: #fff;
`

type CustomInputProps = {
  separateLabel?: boolean
  label: string
}

const CustomInput = ({ separateLabel, label, ...props }: TextFieldProps & CustomInputProps) => {
  const uniqueId = useId()
  return separateLabel ? (
    <>
      <FormLabel htmlFor={uniqueId} style={{ color: "#011727", fontSize: 16, fontWeight: 300 }}>
        {label}
      </FormLabel>
      <TextField
        {...props}
        id={uniqueId}
        variant={"filled"}
        error={!!props.error}
        fullWidth
        inputProps={props.inputProps}
      />
    </>
  ) : (
    <TextField
      {...props}
      variant={props.variant || "outlined"}
      label={label}
      error={!!props.error}
      fullWidth
      inputProps={props.inputProps}
    />
  )
}

function PaymentForm({
  onClose,
  messages,
  formData,
  title,
  advancedPaymentTitle,
}: PaymentFormProps) {
  const [formIsSubmitted, setFormIsSubmitted] = useState(false)
  const [submitState, setSubmitState] = useState<"success" | "fail" | "">("")
  const reCaptchaRef = useRef<ReCAPTCHA | null>()
  const locale = useLocale()
  const schema = generateRentAppartmentPaymentSchema(locale === "he" ? "he" : "en")
  const t = useTranslations("ProjectsPage")
  const tAccordion = useTranslations("AccordionDetails")
  const tRecaptcha = useTranslations("ReCaptcha")
  const [paymentIframeData, setPaymentIframeData] = useState<InitIframeTransactionData | null>(null)

  type FormInput = yup.InferType<typeof schema>
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isSubmitted, isValid, isSubmitting },
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
  })
  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    reCaptchaRef?.current?.reset()
    const recaptchaResponse = (await reCaptchaRef?.current?.executeAsync()) as string
    try {
      if (recaptchaResponse === null) {
        throw new Error("Recaptcha failed")
      }
      const senddata = {
        idNumber: data.idNumber,
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        assetName: formData?.assetName!,
        description: tAccordion("apartment"),
        gRecaptchaResponse: recaptchaResponse,
      } as InitIframeTransactionRequest

      const response = await postWebTransactionsInitIframe(senddata)
      if (response?.data?.body?.status === 1) {
        const { processId, url, processToken } = response.data.body.data

        if (!processId || !url || !processToken) {
          throw new Error("Missing payment data")
        }
        setPaymentIframeData({ processId, url, processToken })
      } else {
        throw new Error("Payment initialization failed")
      }
    } catch (error) {
      reset()
    }
  }

  const handleFail = () => {
    setSubmitState("fail")
    setFormIsSubmitted(true)
  }
  const handleSuccess = (orderId?: string) => {
    setSubmitState("success")
    setFormIsSubmitted(true)
    // Optionally, you can perform additional actions with the orderId
    if (orderId) {
      // Do something with orderId if needed
    }
  }

  return (
    <FormContainer className="newStyle">
      <StyledForm onSubmit={handleSubmit(onSubmit)} className="newStyle">
        <StyledHeader className="newStyle">
          <StyledHeadingH2>
            {advancedPaymentTitle ? advancedPaymentTitle : title ? title : messages.forYou}
          </StyledHeadingH2>
          <IconButton
            sx={{
              flexShrink: 0,
              marginInlineStart: "10px",
              borderRadius: "50%",
              border: "1px solid #F7F7F7",
              background: "rgba(255, 255, 255, 0.50)",
            }}
            onClick={onClose}
          >
            <XIcon width={20} height={20} />
          </IconButton>
        </StyledHeader>

        {paymentIframeData && formIsSubmitted ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              padding: 16,
            }}
          >
            <SuccessFailState
              setIsSubmitted={setFormIsSubmitted}
              state={submitState}
              messages={messages}
              type={"payment-marketing-4-rent"}
              onClose={onClose}
              newStyle
            />
          </div>
        ) : (
          <>
            <StyledInsideForm className="newStyle">
              <StyledTitle>
                <NewText
                  component="p"
                  desktopFontSize={16}
                  desktopFontWeight={400}
                  desktopLineHeight={"150%"}
                  fontFamily={"inherit"}
                  color="black"
                  sx={{ margin: 0 }}
                >
                  {t.rich("advancedPaymentDescription", {
                    strong: (chunks) => <strong>{chunks}</strong>,
                    amount: 2000,
                  })}
                </NewText>
                <NewText
                  component="p"
                  desktopFontSize={14}
                  desktopLineHeight={"14px"}
                  mobileFontSize={14}
                  mobileLineHeight="14px"
                  color={colors.black}
                >
                  {messages.required}
                </NewText>
              </StyledTitle>
              <Grid container columnSpacing={2} rowSpacing={4}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="fullName"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        {...field}
                        separateLabel
                        label={messages.newFullName || messages.fullName}
                        variant="filled"
                        error={!!errors.fullName}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted || isSubmitting}
                            error={!!errors.fullName}
                            text={errors.fullName?.message}
                          />
                        }
                        fullWidth
                        inputProps={{
                          style: {
                            fontSize: 16,
                            lineHeight: "24px",
                            height: 32,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        {...field}
                        separateLabel
                        label={messages.telephone}
                        variant="filled"
                        error={!!errors.phone}
                        inputProps={{
                          style: {
                            fontSize: 16,
                            lineHeight: "24px",
                            height: 32,
                          },
                        }}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted || isSubmitting}
                            error={!!errors.phone}
                            text={errors.phone?.message}
                          />
                        }
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="idNumber"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        {...field}
                        separateLabel
                        label={messages.idNumber || t("idNumber")}
                        variant="filled"
                        error={!!errors.idNumber}
                        inputProps={{
                          style: {
                            fontSize: 16,
                            lineHeight: "24px",
                            height: 32,
                          },
                        }}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted || isSubmitting}
                            error={!!errors.idNumber}
                            text={errors.idNumber?.message}
                          />
                        }
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        {...field}
                        separateLabel
                        label={messages.newEmail || messages.email}
                        variant="filled"
                        error={!!errors.email}
                        inputProps={{
                          style: {
                            fontSize: 16,
                            lineHeight: "24px",
                            height: 32,
                          },
                        }}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted || isSubmitting}
                            error={!!errors.email}
                            text={errors.email?.message}
                          />
                        }
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CheckBoxesContainer>
                    <StyledCheckboxContainer>
                      <Controller
                        name="checkbox1"
                        control={control}
                        render={({ field }) => (
                          <StyledCheckbox
                            {...field}
                            icon={<CheckboxIcon />}
                            checkedIcon={<CheckedCheckboxIcon />}
                            sx={{
                              ":hover": {
                                backgroundColor: "transparent",
                              },
                            }}
                            status={!errors.checkbox1}
                            value={field.value}
                            onChange={() => field.onChange(!field.value)}
                          />
                        )}
                      />
                      <div>
                        <NewText
                          desktopFontSize={14}
                          desktopFontWeight="400"
                          desktopLineHeight="20px"
                          mobileFontSize={12}
                          mobileFontWeight="400"
                          mobileLineHeight={"17px"}
                          component="p"
                          color={"black"}
                          maxWidth={"750px"}
                        >
                          {t.rich("paymentsMarketingMessage", {
                            link: (chunks) => (
                              <a
                                href={
                                  locale === "he"
                                    ? "https://www.ashtromresidencesforrent.co.il/content/registration-form"
                                    : "https://www.ashtromresidencesforrent.co.il/en/content/registration-form"
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "inherit", textDecoration: "underline" }}
                              >
                                {chunks}
                              </a>
                            ),
                          })}
                        </NewText>
                      </div>
                    </StyledCheckboxContainer>
                    <StyledCheckboxContainer>
                      <Controller
                        name="checkbox2"
                        control={control}
                        render={({ field }) => (
                          <StyledCheckbox
                            {...field}
                            icon={<CheckboxIcon />}
                            checkedIcon={<CheckedCheckboxIcon />}
                            sx={{
                              ":hover": {
                                backgroundColor: "transparent",
                              },
                            }}
                            status={true}
                            value={field.value}
                            onChange={() => field.onChange(!field.value)}
                          />
                        )}
                      />

                      <div>
                        <NewText
                          desktopFontSize={14}
                          desktopFontWeight="400"
                          desktopLineHeight="20px"
                          mobileFontSize={12}
                          mobileFontWeight="400"
                          mobileLineHeight={"17px"}
                          component="p"
                          color={"black"}
                          maxWidth={"750px"}
                          dangerouslySetInnerHTML={{
                            __html: t("newCommercialMessage"),
                          }}
                        ></NewText>
                      </div>
                    </StyledCheckboxContainer>
                  </CheckBoxesContainer>
                  <div style={{ paddingTop: "16px" }}>
                    <NewText
                      desktopFontSize={14}
                      desktopFontWeight="400"
                      desktopLineHeight="20px"
                      mobileFontSize={12}
                      mobileFontWeight="400"
                      mobileLineHeight={"17px"}
                      component="p"
                      color={"black"}
                      maxWidth={"750px"}
                      dangerouslySetInnerHTML={{
                        __html: ctaMessage(t("ctaMessage") || ""),
                      }}
                    ></NewText>
                    <NewText
                      desktopFontSize={14}
                      desktopFontWeight="400"
                      desktopLineHeight="20px"
                      mobileFontSize={12}
                      mobileFontWeight="400"
                      mobileLineHeight={"17px"}
                      component="p"
                      color={"black"}
                      maxWidth={"750px"}
                      mt={{
                        xs: "20px",
                      }}
                      sx={{
                        a: {
                          color: "inherit",
                        },
                      }}
                    >
                      <small>
                        {tRecaptcha("protectedBy")}{" "}
                        <a href="https://policies.google.com/privacy">
                          {tRecaptcha("privacyPolicy")}
                        </a>{" "}
                        {tRecaptcha("and")}{" "}
                        <a href="https://policies.google.com/terms">
                          {tRecaptcha("termsOfService")}
                        </a>{" "}
                        {tRecaptcha("apply")}.
                      </small>
                    </NewText>
                  </div>
                </Grid>
              </Grid>
            </StyledInsideForm>
            <BottomContainer className="newStyle" withFormData>
              <TopDetailsDiv>
                <DetailNumbers>
                  <NewText
                    component={"span"}
                    desktopFontSize={18}
                    desktopLineHeight={"23px"}
                    mobileFontSize={18}
                    mobileFontWeight={300}
                    desktopFontWeight={300}
                    mobileLineHeight={"23px"}
                    sx={{
                      letterSpacing: {
                        xs: "-0.36px",
                      },
                    }}
                    style={{ textWrap: "nowrap" }}
                    color={"inherit"}
                  >
                    <span>{tAccordion("building")}</span> {formData.buildingName}
                  </NewText>
                  <SmallDivider />
                  <NewText
                    component={"span"}
                    desktopFontSize={18}
                    desktopLineHeight={"23px"}
                    mobileFontSize={18}
                    mobileFontWeight={300}
                    desktopFontWeight={300}
                    mobileLineHeight={"23px"}
                    sx={{
                      letterSpacing: {
                        xs: "-0.36px",
                      },
                    }}
                    style={{ textWrap: "nowrap" }}
                    color={"inherit"}
                  >
                    <span>{tAccordion("apartment")}</span> {formData.apartmentNumber}
                  </NewText>
                  <SmallDivider />
                  <NewText
                    component={"span"}
                    desktopFontSize={18}
                    desktopLineHeight={"23px"}
                    mobileFontSize={18}
                    mobileFontWeight={300}
                    desktopFontWeight={300}
                    mobileLineHeight={"23px"}
                    sx={{
                      letterSpacing: {
                        xs: "-0.36px",
                      },
                    }}
                    style={{ textWrap: "nowrap" }}
                    color={"inherit"}
                  >
                    <span>{tAccordion("floor")}</span> {formData.floor}
                  </NewText>
                  <SmallDivider mobileHide />
                </DetailNumbers>
                <PriceDiv>
                  <NewText
                    component={"div"}
                    desktopFontSize={18}
                    desktopLineHeight={"150%"}
                    mobileFontSize={16}
                    mobileLineHeight={"150%"}
                    mobileFontWeight={250}
                    desktopFontWeight={250}
                    color={"inherit"}
                    sx={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      paddingInlineStart: { md: "14px" },
                      whiteSpace: "nowrap",
                      letterSpacing: {
                        xs: "-0.32px",
                        md: "-0.36px",
                      },
                    }}
                  >
                    {tAccordion.rich("paymentformPrice", {
                      currency: formData.currency,
                      price: new Intl.NumberFormat(locale).format(formData.price || 0),
                      strong: (chunks) => <PriceStrong>{chunks}</PriceStrong>,
                    })}
                  </NewText>
                </PriceDiv>
              </TopDetailsDiv>

              <BottomDetailsDiv>
                <Box
                  component={BlueStyledButton}
                  type="submit"
                  variant="outlined"
                  size="large"
                  fixedWidth={!!formData}
                  style={{ marginInlineStart: "auto" }}
                  disabled={isSubmitting}
                  onClick={() => {
                    submitClick("שליחה", messages.forYou),
                      (Object.keys(errors).length > 0 || !isValid) &&
                        submitError(
                          "שליחה",
                          messages.forYou,
                          errors?.fullName?.message ||
                            errors?.email?.message ||
                            errors.phone?.message ||
                            "Fields are mandatory",
                        )
                  }}
                >
                  {isSubmitting ? t("sending") : t("continue")}
                </Box>
                {RECAPTCHA_KEY && (
                  <ReCAPTCHA
                    ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
                    sitekey={RECAPTCHA_KEY}
                    size="invisible"
                    hl="iw"
                    tabIndex={-1}
                  />
                )}
              </BottomDetailsDiv>
            </BottomContainer>
          </>
        )}
      </StyledForm>
      <PaymentIFrameRenderer
        handleFail={handleFail}
        handleSuccess={handleSuccess}
        handleClose={() => {
          setFormIsSubmitted(false)
          setSubmitState("")
          setPaymentIframeData(null)
        }}
        paymentIframeData={paymentIframeData}
        formIsSubmitted={formIsSubmitted}
      />
    </FormContainer>
  )
}

export default PaymentForm
